import React, { useRef, useEffect, useMemo, useState } from "react";

import { Navigate, Route, Routes } from "react-router-dom";

import { Layout } from "./shared";

import { useAppSelector, useAppDispatch } from "../app/hooks";
import { Dashboard } from "./pages";

import { ROUTES } from "./constants";
import { createTheme, ThemeProvider } from "@mui/material/styles";

import { selectCurrentLanguage, updateCurrentLanguage } from "./homeSlice";
import type { SupportedLocales } from "./homeSlice";

import * as locales from "@mui/material/locale";
import { defaultTheme } from "../common/MetaSetting";
import { useTranslation } from "react-i18next";
import { LANGUAGE_CONFIG } from "../assets/i18n";
import { LanguageLoader } from "../components/LanguageLoader";

const titleContainer = document.getElementById("title")!;

export const Home = () => {
  const { t, i18n, ready } = useTranslation();
  const currentLanguage = useAppSelector(selectCurrentLanguage);
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(!ready);
  const themeWithLocale = useMemo(() => createTheme(defaultTheme, locales[currentLanguage]), [currentLanguage, defaultTheme]);

  const ref = useRef<ReturnType<typeof setInterval> | null>(null);

  // 语言状态同步和错误处理
  useEffect(() => {
    const syncLanguageState = () => {
      try {
        const i18nLanguage = i18n.language;
        
        // 使用语言映射配置
        const reduxLanguage = LANGUAGE_CONFIG.LANGUAGE_MAP[i18nLanguage as keyof typeof LANGUAGE_CONFIG.LANGUAGE_MAP] || LANGUAGE_CONFIG.LANGUAGE_MAP[LANGUAGE_CONFIG.DEFAULT_LANGUAGE as keyof typeof LANGUAGE_CONFIG.LANGUAGE_MAP];
        
        if (currentLanguage !== reduxLanguage) {
          dispatch(updateCurrentLanguage(reduxLanguage as keyof typeof locales));
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error("语言状态同步失败:", error);
        // 发生错误时使用默认语言
        dispatch(updateCurrentLanguage(LANGUAGE_CONFIG.LANGUAGE_MAP[LANGUAGE_CONFIG.DEFAULT_LANGUAGE as keyof typeof LANGUAGE_CONFIG.LANGUAGE_MAP] as SupportedLocales));
        setIsLoading(false);
      }
    };

    // 监听i18n就绪状态
    if (ready) {
      syncLanguageState();
    }

    // 监听语言变化
    const handleLanguageChange = () => {
      syncLanguageState();
    };

    i18n.on("languageChanged", handleLanguageChange);
    i18n.on("initialized", handleLanguageChange);

    ref.current = setInterval(() => {}, 60000);

    return () => {
      i18n.off("languageChanged", handleLanguageChange);
      i18n.off("initialized", handleLanguageChange);
    };
  }, [i18n, dispatch, currentLanguage, ready]);

  titleContainer.innerHTML = t("webTitle");

  return (
    <>
      <LanguageLoader 
        isLoading={isLoading} 
        message={t('loadingLanguage', 'Loading language resources...')}
      />
      <ThemeProvider theme={themeWithLocale}>
        <Layout>
          <Routes>
            <Route path={ROUTES.root} element={<Dashboard />} />
          </Routes>
        </Layout>
      </ThemeProvider>
    </>
  );
};

export default Home;
